import {
  isAnswerCorrect,
  isAnswerPartial,
  numericListPartialCredit,
  multiPartListPartialCredit,
  arrowSequencePositionalCredit,
  submissionIsDeterministicallyWrong,
  submittedMissesPercentSign,
  correctIsClockTime,
  timesMatch,
  questionAsksForClockTime,
  clockTimeInDisplayedRange,
  fractionIsInSimplestForm,
  decimalMissesRequiredFractionForm,
  submissionMissesSchemeComparisonChoice,
  submissionMissesWorkedComparisonChoice,
  statedAreaWhenQuestionAsksForArea,
  parseSimpleNumericValue,
  letterXSubmittedForTimesSymbol,
  workedDecisionExplanationAgrees,
} from "../../../utils/answerMatchUtils";
import { isFillQuestionType, parsePipeJoinedBlanks } from "../../../utils/fillQuestionUtils";

// The Result page and the Review page render the same leaf rows from the same server context, but each
// used to re-derive its own verdict and marks. They drifted: only Result applied the equivalence
// upgrade, only Review let the AI overturn a rule verdict on a non-text row, and Review ignored the
// server partial_ratio (flat 0.5 for every partial). This resolver is the Result page's semantics —
// rule verdict first, AI consulted only for free-text rows — used by both pages so they agree.

const TEXT_TYPE_HINTS = ["short_answer", "short-answer", "essay", "short answer", "text"];

const barePercentToken = (value) => {
  const plain = String(value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const match = plain.match(/^(-?\d+(?:\.\d+)?)\s*(?:%|per\s*cent)$/);
  return match ? `${match[1]}%` : null;
};

const sameWrittenPercent = (submitted, correct) => {
  const left = barePercentToken(submitted);
  const right = barePercentToken(correct);
  return left != null && left === right;
};

// A "%" chip really beside the box means a retyped sign stays wrong. A "which of 2%, 1/5…"
// stem prints that sign as the choice, so a leftover chip must not keep the mark Incorrect.
const rowShowsPercentChip = (row) => {
  if (String(row.answer_data?.answer_unit ?? "").trim() !== "%") return false;
  const token = barePercentToken(row.correct_answer_label);
  const plain = [row.question_text, row.passage_stem, row.passageStem]
    .filter(Boolean)
    .join(" ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!token || !/\bwhich\s+of\b/.test(plain)) return true;
  const collapsed = plain.replace(/\s+/g, "").replace(/\\/g, "");
  let from = 0;
  while (from < collapsed.length) {
    const at = collapsed.indexOf(token, from);
    if (at < 0) return true;
    const before = at > 0 ? collapsed[at - 1] : "";
    if (!/[0-9.]/.test(before)) return false;
    from = at + 1;
  }
  return true;
};

const normType = (row) => String(row.question_type || "").toLowerCase().replace(/_/g, "-");

const rowIsText = (row) => {
  if (typeof row.is_text === "boolean") return row.is_text;
  const qt = normType(row);
  return TEXT_TYPE_HINTS.some((t) => qt.includes(t));
};

const rowIsAnswered = (row) => {
  if (row.answered === false) return false;
  if (row.answered === true) return true;
  return (row.your_answer != null && String(row.your_answer).trim() !== "")
    || !!row.your_answer_drawing
    || !!row.your_answer_interactive;
};

// Unit/currency/ordinal noise the grader is stricter about than a human marker would be. Kept narrow:
// it only ever upgrades an exact match after normalisation, and never runs once detWrong fires.
const equivalentAfterNormalisation = (submitted, correct) => {
  if (!submitted || !correct) return false;
  if (String(submitted).toLowerCase().includes(" m") && String(correct).toLowerCase().includes("cm")
    && !/\bcm\b/i.test(String(submitted))) return false;
  const norm = (v) => String(v ?? "").toLowerCase().trim()
    .replace(/÷/g, "/")
    .replace(/[£$€°]/g, "").replace(/\s*(am|pm)\s*$/i, "")
    .replace(/^(\d{1,2})\.(\d{2})$/, "$1:$2")
    .replace(/^(\d+)(?:st|nd|rd|th)(?:\s+\w+)*$/, "$1")
    .replace(/^(\d+(?:\.\d+)?)\s*p$/, "$1")
    .replace(/\s*\b(?:km|cm|mm|mg|kg|ml|litres?|liters?|metres?|meters?)\s*$/i, "")
    .trim();
  const alts = String(correct).includes(" (or) ")
    ? String(correct).split(" (or) ").map((p) => p.trim())
    : [String(correct)];
  return alts.some((a) => norm(a) === norm(submitted));
};

export const resolveLeafVerdict = (row, { aiFeedbackMap, aiFeedbackStatus } = {}) => {
  const max = Number(row.marks ?? 0);
  const answered = rowIsAnswered(row);

  const blankCount = row.answer_data?.correct_blanks?.length
    || row.answer_data?.blank_labels?.length
    || parsePipeJoinedBlanks(row.correct_answer_label).length;
  // image-fill is a multi-blank fill too — isFillQuestionType leaves it out because the display code
  // renders it through its own overlay path, not because the server grades it any differently.
  const isMultiBlankFill = (isFillQuestionType(normType(row)) || normType(row) === "image-fill")
    && blankCount > 1;

  // The server's `is_text` means "accepts a typed payload", which multi-blank fills do — but it graded
  // each of their boxes itself (pooled blanks, answer_variants, partial credit). Treating them as text
  // rows here handed the row to the AI, whose whole-answer read of "5 | 1000" against "5, 1000" printed
  // "AI Incorrect" over a fully correct fill.
  const isText = rowIsText(row) && !isMultiBlankFill;

  // "Answer must be written exactly like this" — the server compared it literally, so neither the
  // fuzzy client fallback nor the normalisation upgrade below may reopen the verdict.
  const exactOnly = !!(row.exact_match || row.answer_data?.exact_match);

  // An authored answer rule graded this row against its whole accepting set ("any tile differing from
  // the small white triangle in exactly one way"). `correct_answer_label` is then the rule's PROSE
  // description of that set, not a value to compare against — every matcher below reads it as one
  // answer and calls a right answer wrong. "small white square" was marked Incorrect after submit for
  // exactly this reason, over a server row that said correct. The rule verdict is final here.
  const ruleGraded = !!(row.answer_rule || row.answer_data?.answer_rule);

  const ruleIsCorrect = row.is_correct == null
    ? (!exactOnly && !isText && !isMultiBlankFill && isAnswerCorrect(row.your_answer, row.correct_answer_label, { questionText: row.question_text }))
    : !!row.is_correct;
  const ruleIsPartial = !ruleIsCorrect
    && (!!row.is_partial
      || (row.is_correct == null && !exactOnly && !isText && !isMultiBlankFill && isAnswerPartial(row.your_answer, row.correct_answer_label)));

  // Multi-blank fills are excluded for the reason StudentPaperTake already excludes them: `your_answer`
  // is the boxes joined with "|" and `correct_answer_label` is a display string listing every box's
  // value, so a whole-answer guard reads the two lists as ONE value. answerSetMatchesExactly does not
  // even count "|" as a separator on the submitted side, so it tore "39 | 187 | 112" into 5 tokens and
  // called every pipe-joined fill deterministically wrong — a fully correct one included. That verdict
  // then overrode the server's, which had already graded each box (pooled rows, answer_variants and
  // partial credit included — none of which a single-string client matcher can model).
  // A tick-all-that-apply row's `your_answer` is the ticked labels joined with ", " and its
  // `correct_answer_label` lists every correct one, so a whole-answer guard reads a partial tick set
  // ("(3,2), (5,4)" against "(3,2), (5,4), (10,9)") as one mismatched value. The server compared the
  // ticked SET and already scored it "2 of 3"; nothing here may overturn or re-derive that.
  const isMultiSelect = normType(row) === "multiple-select";

  // A drawing row's `your_answer` is only the TYPED half of a two-part answer ("mark D on the diagram
  // AND write down its coordinates") — the plotted figure is the other half and never reaches the
  // client, so `correct_answer_label` ("(5, 2)") describes one half against a submission that is one
  // half. A whole-answer guard reading those two as the entire answer overturned the server's honest
  // half mark: D plotted in exactly the right place with the coordinates mistyped came back as a flat
  // Incorrect. The server graded the figure and the text separately and already scored it "1 of 2".
  const isDrawing = normType(row) === "drawing";

  // detWrong exists to stop the AI upgrading a row the rules already refused — it was never meant to
  // overturn a row the SERVER graded correct. It did: "12/5" for a "2 2/5" mark scheme is graded correct
  // by gradeExtractTextAnswer (the stem never asked for a mixed number) and Check answer agreed, then
  // this single-string client guard re-read the two as one value and flipped it to Incorrect after
  // submit. The server saw the answer_variants, the answer rule and the pooled blanks; this guard sees
  // a string. An explicit server Correct is final.
  const statedArea = statedAreaWhenQuestionAsksForArea(
    row.question_text,
    row.answer_explanation,
    row.correct_answer_label
  );
  const statedAreaNum = statedArea ? parseSimpleNumericValue(statedArea) : null;
  const submittedAreaNum = statedArea ? parseSimpleNumericValue(row.your_answer) : null;
  const areaMatch = statedAreaNum != null && submittedAreaNum != null
    && Math.abs(statedAreaNum - submittedAreaNum) < 0.0001;

  const serverSaysCorrect = row.is_correct === true;
  const detWrong = !areaMatch && !serverSaysCorrect && !isMultiBlankFill && !isMultiSelect && !isDrawing && !ruleGraded && !!(row.your_answer && row.correct_answer_label
    && (submissionIsDeterministicallyWrong(row.your_answer, row.correct_answer_label, row.question_text)
      || submittedMissesPercentSign(row.your_answer, row.correct_answer_label, row)));

  // A value-set list ("2, 3, 5, 7, …") that misses one value or repeats one is deterministically wrong
  // as a whole answer — that is exactly why it lands here as a flat Incorrect. Score the values instead.
  const listPartial = (!isMultiBlankFill && !isMultiSelect && !isDrawing && !exactOnly && !ruleGraded && !ruleIsCorrect)
    ? (arrowSequencePositionalCredit(row.your_answer, row.correct_answer_label)
      || numericListPartialCredit(row.your_answer, row.correct_answer_label)
      || multiPartListPartialCredit(row.your_answer, row.correct_answer_label))
    : null;

  const aiq = aiFeedbackMap?.[String(row.question_id)];
  // The AI only ever decides a free-text row the rules could not grade. An explicit rule Incorrect
  // (or a bare-numeric mismatch the deterministic guard already caught) must not be upgraded by a
  // contradictory AI "Correct" — that is what made Q21c (6/24 vs 1/6) show green after submit.
  const ruleExplicitIncorrect = row.is_correct === false;
  const aiReady = isText && !detWrong && !exactOnly && !ruleGraded && !ruleExplicitIncorrect
    && aiFeedbackStatus === "completed" && !!aiq;
  const aiRatio = aiq?.partial_score != null ? Number(aiq.partial_score) : null;
  const aiIsPartial = aiRatio != null && aiRatio > 0 && aiRatio < 1;

  // Same reason detWrong is off for a drawing row, in the other direction: matching the typed half
  // against the label would hand FULL marks to an answer whose figure is wrong — "(5, 2)" typed
  // correctly under a D plotted in the wrong square is one mark of two, not two.
  const isEquiv = !ruleIsCorrect && !isText && !isDrawing && !detWrong && !exactOnly
    && equivalentAfterNormalisation(row.your_answer, row.correct_answer_label);
  // Short-answer clock times ("16.25" for "16:25") are text rows, so the unit-noise isEquiv gate
  // above never fires — and a stale server Incorrect (or an old detWrong ratio false-positive) then
  // stuck on Review/Result even though Check answer and the clock matcher agree.
  const clockEquiv = !ruleIsCorrect && !isDrawing && !detWrong && !exactOnly && !ruleGraded
    && !!row.your_answer && !!row.correct_answer_label
    && correctIsClockTime(row.correct_answer_label)
    && timesMatch(row.your_answer, row.correct_answer_label, questionAsksForClockTime(row.question_text));
  // A grouped leaf ("The smallest") stores Incorrect while both cards show "2%". The sign is
  // the printed choice, not a unit retyped beside a chip. A real "%" chip (Whitgift) stays put.
  const percentChoiceEquiv = !ruleIsCorrect && !isDrawing && !detWrong && !exactOnly && !ruleGraded
    && sameWrittenPercent(row.your_answer, row.correct_answer_label)
    && !rowShowsPercentChip(row);
  // "Did Lorenzo pass? Show your working." Stored Incorrect, same verdict and figures.
  const decisionCard = String(row.correct_answer_label ?? "");
  const decisionScheme = /\d/.test(decisionCard)
    ? decisionCard
    : [decisionCard, row.answer_explanation].filter(Boolean).join("\n");
  const workedDecisionEquiv = !ruleIsCorrect && !isDrawing && !detWrong && !exactOnly && !ruleGraded
    && workedDecisionExplanationAgrees(
      row.your_answer,
      decisionScheme,
      [row.question_text, row.passage_stem || row.passageStem].filter(Boolean).join("\n")
    );
  const clockRangeEquiv = !ruleIsCorrect && !isDrawing && !detWrong && !exactOnly
    && !!row.your_answer && !!row.correct_answer_label
    && clockTimeInDisplayedRange(
      row.your_answer,
      row.correct_answer_label,
      questionAsksForClockTime(row.question_text)
    );
  const unreducedSchemeMatch = !ruleIsCorrect && !isDrawing && !detWrong && !exactOnly
    && !!row.your_answer && !!row.correct_answer_label
    && !fractionIsInSimplestForm(row.correct_answer_label)
    && isAnswerCorrect(row.your_answer, row.correct_answer_label, { questionText: row.question_text });

  // Working that never names the choice (Q13c reason-first, Q21/Q23 deal name missing) is half
  // an answer. The server and Check answer already demote it; Review/Result must not restore
  // a stored or AI "Correct" over that split.
  const missesConclusion = !exactOnly && !ruleGraded && !isMultiBlankFill && !isDrawing
    && !!row.your_answer && !!row.correct_answer_label
    && (submissionMissesSchemeComparisonChoice(row.your_answer, row.correct_answer_label, row.question_text)
      || submissionMissesWorkedComparisonChoice(row.your_answer, row.correct_answer_label));

  const areaConflict = statedAreaNum != null && submittedAreaNum != null && !areaMatch;
  const timesSymbolConflict = letterXSubmittedForTimesSymbol(
    row.your_answer,
    row.correct_answer_label,
    row.answer_data?.correct_blanks
  );
  const fractionStem = [row.question_text, row.passage_stem || row.passageStem].filter(Boolean).join("\n");
  const fractionFormConflict = !exactOnly && !isMultiBlankFill && !isMultiSelect && !isDrawing
    && decimalMissesRequiredFractionForm(row.your_answer, row.correct_answer_label, fractionStem);
  const correct = !detWrong && !fractionFormConflict && !missesConclusion && !areaConflict && !timesSymbolConflict && answered
    && (ruleIsCorrect || areaMatch || isEquiv || percentChoiceEquiv || workedDecisionEquiv || clockEquiv || clockRangeEquiv || unreducedSchemeMatch || (aiReady && aiq.is_correct === true));
  // A text row's ruleIsPartial can only be the SERVER's verdict (the client branch above is already
  // excluded for them), and the server is exactly who scored "triangle" for "Equilateral triangle" as
  // half a mark — dropping it here is how that partial became a zero after submit.
  const partial = !correct && answered
    && (!!listPartial || missesConclusion || (!detWrong && (ruleIsPartial || (aiReady && aiIsPartial))));

  // Whether the AI verdict is what decided this row, so the UI can credit the right grader. A row the
  // rules graded is not the AI's ("AI Incorrect" was printed over deterministic multi-fill verdicts,
  // which reads as the AI having overruled a correct answer when it never looked at it).
  const aiDecided = aiReady && (aiq.is_correct === true || aiIsPartial || ruleIsCorrect === false);

  if (!answered) return { result: "unanswered", earned: 0, max, isEquiv: false, aiDecided: false };
  if (correct) {
    return {
      result: "correct",
      earned: max,
      isEquiv: (isEquiv || clockEquiv || clockRangeEquiv || unreducedSchemeMatch) && !ruleIsCorrect,
      max,
      aiDecided: !ruleIsCorrect && aiReady && aiq.is_correct === true,
    };
  }
  if (partial) {
    const serverRatio = Number(row.partial_ratio ?? 0);
    const ratio = Math.max(serverRatio, aiIsPartial ? aiRatio : 0, listPartial?.ratio ?? 0) || 0.5;
    return {
      result: "partial",
      earned: Math.round(ratio * max * 100) / 100,
      max,
      isEquiv: false,
      aiDecided: !ruleIsPartial && !listPartial && aiReady && aiIsPartial,
    };
  }
  // An exact_match or rule-graded row was graded outright by the server — it is never "awaiting a
  // human/AI read".
  if (isText && !detWrong && !aiReady && !exactOnly && !ruleGraded) return { result: "written", earned: 0, max, isEquiv: false, aiDecided: false };
  return { result: "incorrect", earned: 0, max, isEquiv: false, aiDecided };
};

export const sumLeafEarned = (rows, ctx) =>
  rows.reduce((s, r) => s + Number(resolveLeafVerdict(r, ctx).earned ?? 0), 0);
